import {
    DeltaType,
    MetricConfig,
    PeriodResolution,
} from "@/features/Metrics/Metrics.types";
import { MetricQueryValue } from "./MetricQueryValue";
import { DeltaValue } from "./DeltaValue";
import { MetricQueryString } from "@/entities/Finance/models/MetricQueryString.model";
import {
    availableDataRangeAtom,
    getMetricsByCodeAtom,
} from "@/features/Metrics/state/Metrics.state";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo } from "react";
import { DetailGridValue } from "./DetailGridValue";
import { formatMetricValue } from "@/features/Metrics/helpers/formatMetricValue";
import {
    AIMContextViewAtom,
    AIMViewType,
} from "../../../panels/AIMPanelContext";
import { financeMetricsAtom } from "@/features/Metrics/state/MetricQueries.state";
import { DetailGridLabel } from "./DetailGridLabel";

export interface DetailGridMetricProps {
    config: MetricConfig;
    query: MetricQueryString;
    deltaType: DeltaType;
    panelTitle?: string;
}

export function DetailGridMetricQuery({
    config,
    query,
    deltaType,
    panelTitle,
}: DetailGridMetricProps) {
    return (
        <>
            <DetailGridLabel label={config.label} />

            <MetricQueryValue
                query={query}
                config={config}
                panelTitle={panelTitle}
            />

            <DeltaValue deltaType={deltaType} config={config} />
        </>
    );
}

export function DetailGridMetric({
    resolution,
    config,
    deltaType,
}: {
    resolution: PeriodResolution;
    config: MetricConfig;
    deltaType: DeltaType;
    panelTitle?: string;
}) {
    const availableDataRange = useAtomValue(availableDataRangeAtom);
    const { isLoading } = useAtomValue(financeMetricsAtom);
    const getMetricsByCode = useAtomValue(getMetricsByCodeAtom);
    const setContextView = useSetAtom(AIMContextViewAtom);
    const metric = useMemo(
        () => getMetricsByCode([config.code], resolution)?.get(config.code),
        [config.code, resolution, getMetricsByCode],
    );
    const lastClosedMonth = availableDataRange.data?.to;
    const data = useMemo(() => {
        if (!lastClosedMonth || !metric) return undefined;
        return metric.toTabular()[lastClosedMonth.getShortName()];
    }, [lastClosedMonth, metric]);

    return (
        <>
            <DetailGridLabel label={config.label} />
            <DetailGridValue
                isLoading={isLoading || availableDataRange.isLoading}
                value={
                    data
                        ? formatMetricValue(data.getValue(), config.type)
                        : undefined
                }
                onClick={() =>
                    data &&
                    metric &&
                    lastClosedMonth &&
                    setContextView({
                        type: AIMViewType.METRIC_LINEAGE,
                        props: {
                            metric,
                            period: lastClosedMonth,
                        },
                        isExpanded: true,
                    })
                }
            />
            <DeltaValue deltaType={deltaType} config={config} />
        </>
    );
}
